/**
 * The dash characters Sona must never read or write: em dash, en dash, and the
 * horizontal bar that renders identically to an em dash. Models imitate the
 * punctuation of their own context, so this one set backs the prompt-source
 * guards, the runtime normaliser and the transcript eval alike; a second copy
 * anywhere would let them drift apart.
 *
 * ADR-159 tabulates the same code points and a drift test holds the two equal.
 */
export const BANNED_DASH_CODE_POINTS = [0x2014, 0x2013, 0x2015] as const;

/** The banned code points as a string, for building character classes. */
export const BANNED_DASH_CHARACTERS: string = String.fromCodePoint(...BANNED_DASH_CODE_POINTS);

const DASH = `[${BANNED_DASH_CHARACTERS}]`;

const ANY_DASH = new RegExp(DASH, 'u');
const DASH_RUN = new RegExp(`${DASH}+`, 'gu');
const EN_DASH = '–';
const HSPACE = /[^\S\n]/u;
const DIGIT = /\d/u;
const WORD_CHAR = /[\p{L}\p{N}]/u;
const CLOSING_PUNCTUATION = /[.,!?;:)\]]/u;
const OPENING_PUNCTUATION = /[,;:]/u;
const OPENING_BRACKET = /[([]/u;

/** True when `text` contains any banned dash. */
export function containsBannedDash(text: string): boolean {
  return ANY_DASH.test(text);
}

type RangeEndpoint = { kind: 'weekday' | 'month'; name: string };

function endpoints(
  kind: RangeEndpoint['kind'],
  table: Record<string, readonly string[]>,
): Map<string, RangeEndpoint> {
  const map = new Map<string, RangeEndpoint>();
  for (const [name, forms] of Object.entries(table)) {
    for (const form of [name, ...forms]) map.set(form, { kind, name });
  }
  return map;
}

// Capitalised forms only: "Sat", "Sun", "Wed", "Mar" and "May" are also
// ordinary words, so a lowercase match is never treated as a range endpoint.
const RANGE_ENDPOINTS = new Map<string, RangeEndpoint>([
  ...endpoints('weekday', {
    Monday: ['Mon'],
    Tuesday: ['Tue', 'Tues'],
    Wednesday: ['Wed', 'Weds'],
    Thursday: ['Thu', 'Thur', 'Thurs'],
    Friday: ['Fri'],
    Saturday: ['Sat'],
    Sunday: ['Sun'],
  }),
  ...endpoints('month', {
    January: ['Jan'],
    February: ['Feb'],
    March: ['Mar'],
    April: ['Apr'],
    May: [],
    June: ['Jun'],
    July: ['Jul'],
    August: ['Aug'],
    September: ['Sep', 'Sept'],
    October: ['Oct'],
    November: ['Nov'],
    December: ['Dec'],
  }),
]);

// Longer than any endpoint form ("Wednesdays"), so a word scan stops early.
const MAX_ENDPOINT_LENGTH = 10;

/** The word ending at `end`, or null when none starts after `floor` or it is too long. */
function wordEndingAt(text: string, end: number, floor: number): string | null {
  let i = end;
  while (i > floor && WORD_CHAR.test(text.charAt(i - 1))) {
    if (end - i >= MAX_ENDPOINT_LENGTH) return null;
    i--;
  }
  return i < end ? text.slice(i, end) : null;
}

/** The word starting at `start`, or null when there is none or it is too long. */
function wordStartingAt(text: string, start: number): string | null {
  let i = start;
  while (i < text.length && WORD_CHAR.test(text.charAt(i))) {
    if (i - start >= MAX_ENDPOINT_LENGTH) return null;
    i++;
  }
  return i > start ? text.slice(start, i) : null;
}

/** A weekday or month name, with a plural full weekday name kept plural. */
function rangeEndpoint(word: string): (RangeEndpoint & { spoken: string }) | null {
  const exact = RANGE_ENDPOINTS.get(word);
  if (exact) return { ...exact, spoken: exact.name };
  const singular = word.endsWith('s') ? RANGE_ENDPOINTS.get(word.slice(0, -1)) : undefined;
  if (singular?.kind === 'weekday' && singular.name === word.slice(0, -1))
    return { ...singular, spoken: word };
  return null;
}

/** True when the word ending just before `end` contains a digit. */
function wordBeforeHasDigit(text: string, end: number): boolean {
  for (let i = end - 1; i >= 0 && WORD_CHAR.test(text.charAt(i)); i--) {
    if (DIGIT.test(text.charAt(i))) return true;
  }
  return false;
}

/** True when the word starting at `start` contains a digit. */
function wordAfterHasDigit(text: string, start: number): boolean {
  for (let i = start; i < text.length && WORD_CHAR.test(text.charAt(i)); i++) {
    if (DIGIT.test(text.charAt(i))) return true;
  }
  return false;
}

/**
 * Rewrites banned dashes as the punctuation a dash usually stands in for.
 * A single dash between two weekday names or two month names, spaced or not,
 * is a named range and is written out in full ("Mon – Thu" becomes "Monday
 * through Thursday"). Otherwise, a dash with no space either side, joining
 * two words:
 * - becomes "to" when both words carry a digit, since that is a range
 *   ("10am–2pm", "1990s—2000s", "3–4");
 * - becomes a hyphen when it is an en dash, which joins a compound or a route
 *   ("pre–war", "London–Paris") and must not turn it into a list;
 * - otherwise (an em dash or horizontal bar between plain words) is an aside
 *   and becomes a comma, as models write "willpower—it is".
 * A spaced dash between words that both carry a digit is also a range
 * ("10am – 2pm" becomes "10am to 2pm"). Any other dash is dropped
 * at the start or end of a line or beside punctuation, and becomes a comma
 * elsewhere. Newlines are never consumed and text without a banned dash is
 * returned unchanged.
 *
 * Memory facts are stored after this runs, so a rewrite that changes meaning
 * (a range read as a list) outlives the code that made it.
 *
 * A single pass over the dash runs, widening each across its neighbouring
 * spaces by hand: a regex with optional whitespace on both sides of the dash
 * backtracks quadratically over long runs of spaces, and this runs on every
 * prompt variable and voice-tool response. The word scans stop at the first
 * non-word character, so each character is read a bounded number of times.
 */
export function normaliseBannedDashes(text: string): string {
  if (!containsBannedDash(text)) return text;
  let out = '';
  let cursor = 0;
  for (const match of text.matchAll(DASH_RUN)) {
    const dashStart = match.index;
    const dashEnd = dashStart + match[0].length;
    let start = dashStart;
    while (start > cursor && HSPACE.test(text.charAt(start - 1))) start--;
    let end = dashEnd;
    while (end < text.length && HSPACE.test(text.charAt(end))) end++;
    const previous = start > 0 ? text.charAt(start - 1) : '\n';
    const next = end < text.length ? text.charAt(end) : '\n';
    const single = match[0].length === 1;
    // A dash between two weekdays or two months, spaced or not, is a named
    // range: "Mon – Thu" read as "Mon, Thu" would store two days, not four.
    if (single) {
      const before = wordEndingAt(text, start, cursor);
      const after = wordStartingAt(text, end);
      const from = before && rangeEndpoint(before);
      const to = after && rangeEndpoint(after);
      if (from && to && from.kind === to.kind) {
        out += `${text.slice(cursor, start - before.length)}${from.spoken} through ${to.spoken}`;
        cursor = end + after.length;
        continue;
      }
    }
    const joinsWords =
      single &&
      start === dashStart &&
      end === dashEnd &&
      WORD_CHAR.test(previous) &&
      WORD_CHAR.test(next);
    // A single dash whose neighbouring words both carry a digit is a range,
    // spaced or not: "10am – 2pm" read as an aside would store "10am, 2pm",
    // and "3 — 4 beers" would store "3, 4 beers", each a different fact.
    const isRange =
      single &&
      WORD_CHAR.test(previous) &&
      WORD_CHAR.test(next) &&
      wordBeforeHasDigit(text, start) &&
      wordAfterHasDigit(text, end);
    let replacement: string;
    if (isRange) replacement = ' to ';
    else if (joinsWords && match[0] === EN_DASH) replacement = '-';
    else if (
      previous === '\n' ||
      next === '\n' ||
      CLOSING_PUNCTUATION.test(next) ||
      OPENING_BRACKET.test(previous)
    )
      replacement = '';
    else if (OPENING_PUNCTUATION.test(previous)) replacement = ' ';
    else replacement = ', ';
    out += text.slice(cursor, start) + replacement;
    cursor = end;
  }
  return out + text.slice(cursor);
}
